import { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import MainLayout from "Layouts/MainLayout/MainLayout";
import { Button } from "Components/Common/Button/Button";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { ApiError } from "Components/Common/ApiError";
import ClassNames from "Helpers/Common";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import { ReactComponent as BackArrowIcon } from "Assets/BackArrow.svg";
import {
  useSaveAndPublishVisitType,
  useUpdateVisitType,
  useVisitType,
  useVisitTypeMemberships,
} from "CustomHooks/useVisitTypes";
import { VISIT_TYPES } from "Constants/Views";
import { normalizeCheckout } from "./constants";
import { QuestionsTab } from "./QuestionsTab";
import { CheckoutTab } from "./CheckoutTab";
import { SettingsTab } from "./SettingsTab";
import { VersionsTab } from "./VersionsTab";

const TABS = ["Questions", "Checkout", "Settings", "Versions"];

export const VisitTypeEditorPage = () => {
  const { id } = useParams();
  const history = useHistory();

  const [apiError, setApiError] = useState("");
  const [activeTab, setActiveTab] = useState("Questions");
  const visitTypeQuery = useVisitType(id);
  const membershipsQuery = useVisitTypeMemberships();
  const updateVisitType = useUpdateVisitType();
  const saveAndPublishVisitType = useSaveAndPublishVisitType(id);
  const visitType = visitTypeQuery.data;
  const memberships = membershipsQuery.data || [];

  // Editable draft state
  const [questions, setQuestions] = useState([]);
  const [checkout, setCheckout] = useState(normalizeCheckout(null));
  const [settings, setSettings] = useState({
    name: "",
    description: "",
    icon: null,
    allowFreeTextReason: false,
  });
  const [dirty, setDirty] = useState(false);
  const [publishProblems, setPublishProblems] = useState([]);
  const initializedId = useRef(null);

  const applyServerState = (serverType) => {
    setQuestions(serverType.draft?.questions || []);
    setCheckout(normalizeCheckout(serverType.checkout));
    setSettings({
      name: serverType.name || "",
      description: serverType.description || "",
      icon: serverType.icon || null,
      allowFreeTextReason: !!serverType.behavior?.allowFreeTextReason,
    });
    setDirty(false);
  };

  useEffect(() => {
    if (visitType && initializedId.current !== id) {
      applyServerState(visitType);
      initializedId.current = id;
    }
  }, [id, visitType]);

  const buildPayload = () => ({
    name: settings.name,
    description: settings.description,
    icon: settings.icon,
    behavior: { allowFreeTextReason: settings.allowFreeTextReason },
    checkout,
    draft: { questions },
  });

  // Counts edits so a save only clears "dirty" if nothing changed while the
  // request was in flight — edits typed during a save are never lost or
  // wrongly marked as saved.
  const editCounter = useRef(0);

  const saveDraft = () => {
    const editsAtSave = editCounter.current;
    setPublishProblems([]);
    return updateVisitType.mutateAsync({ id, payload: buildPayload() })
      .then(() => {
        if (editCounter.current === editsAtSave) setDirty(false);
        return true;
      })
      .catch((err) => {
        setApiError(errorFormatter(err?.response?.data?.errors || err?.message));
        return false;
      });
  };

  const publish = async () => {
    const editsAtSave = editCounter.current;
    setPublishProblems([]);
    try {
      await saveAndPublishVisitType.mutateAsync(buildPayload());
      if (editCounter.current === editsAtSave) setDirty(false);
    } catch (err) {
      const problems = err?.response?.data?.errors;
      if (Array.isArray(problems) && problems.length) {
        setPublishProblems(problems);
      } else {
        setApiError(errorFormatter(problems || err?.message));
      }
    }
  };

  const markDirty = () => {
    editCounter.current += 1;
    setDirty(true);
    setPublishProblems([]);
  };

  // Show the spinner on the first load AND while the list is still refetching
  // with no match yet — right after creating a type the list cache doesn't
  // include it until the invalidated query settles, which otherwise flashes
  // "Visit type not found" for a moment.
  if (visitTypeQuery.isLoading || (visitTypeQuery.isFetching && !visitType)) {
    return (
      <MainLayout>
        <div className="p-10">
          <Spinner />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="p-1 rounded hover:bg-gray-100"
              onClick={() => history.push(VISIT_TYPES)}
            >
              <BackArrowIcon className="h-5 w-5 text-gray-500" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                {settings.name || visitType?.name}
                {visitType?.publishedVersion > 0 ? (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">
                    Published v{visitType.publishedVersion}
                  </span>
                ) : (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                    Never published
                  </span>
                )}
                {dirty && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">
                    Unsaved changes
                  </span>
                )}
              </h1>
              <p className="text-sm text-gray-500">{visitType?.slug}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              btnText="Save Draft"
              variant="Secondary"
              className="h-10 px-4"
              isLoading={updateVisitType.isLoading}
              isDisabled={!dirty || updateVisitType.isLoading || saveAndPublishVisitType.isLoading}
              onClick={saveDraft}
            />
            <Button
              btnText="Publish"
              className="h-10 px-4"
              isLoading={saveAndPublishVisitType.isLoading}
              isDisabled={updateVisitType.isLoading || saveAndPublishVisitType.isLoading}
              onClick={publish}
            />
          </div>
        </div>

        {(apiError || visitTypeQuery.error) && (
          <ApiError
            error={apiError || errorFormatter(
              visitTypeQuery.error?.response?.data?.errors || visitTypeQuery.error?.message
            )}
            setApiError={setApiError}
          />
        )}
        {!visitTypeQuery.error && !visitTypeQuery.isFetching && !visitType && (
          <ApiError error="Visit type not found" setApiError={setApiError} />
        )}

        {publishProblems.length > 0 && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3">
            <h3 className="text-sm font-semibold text-red-800">
              Cannot publish — fix these first:
            </h3>
            <ul className="mt-1 list-disc list-inside text-sm text-red-700">
              {publishProblems.map((problem, index) => (
                <li key={index}>{problem}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-6">
            {TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={ClassNames(
                  "py-2 px-1 border-b-2 text-sm font-medium",
                  activeTab === tab
                    ? "border-blue-chill text-blue-chill"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                )}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === "Questions" && (
          <QuestionsTab
            questions={questions}
            onChange={(nextQuestions) => {
              setQuestions(nextQuestions);
              markDirty();
            }}
          />
        )}
        {activeTab === "Checkout" && (
          <CheckoutTab
            checkout={checkout}
            memberships={memberships}
            hasRefillPicker={questions.some((q) => q.widget === "REFILL_MED_PICKER")}
            onChange={(nextCheckout) => {
              setCheckout(nextCheckout);
              markDirty();
            }}
          />
        )}
        {activeTab === "Settings" && (
          <SettingsTab
            visitType={visitType}
            settings={settings}
            onChange={(nextSettings) => {
              setSettings(nextSettings);
              markDirty();
            }}
            setApiError={setApiError}
          />
        )}
        {activeTab === "Versions" && (
          <VersionsTab
            visitTypeId={id}
            publishedVersionId={visitType?.publishedVersionId}
          />
        )}
      </div>
    </MainLayout>
  );
};
